<template>
  <div v-loading="loading" class="search-page">
    <div class="search-page__top">
      <div class="search-page__title">AB Testing</div>
      <div class="search-page__top-actions">
        <el-input
          :prefix-icon="Search"
          v-model="filter.search"
          placeholder="Search"
        />
        <el-button
          type="success"
          :icon="Plus"
          :disabled="isViewer"
          @click="addTest"
          >Add Test</el-button
        >
      </div>
    </div>
    <div class="search-page__main">
      <div class="search-page__content">
        <div class="search-page__content-top">
          <div class="shelf-form__platforms">
            <el-button
              class="shelf-form__platform"
              v-for="platform in platforms"
              :key="platform.id"
              size="small"
              :type="filter.platforms.has(platform.id) ? 'primary' : ''"
              plain
              @click="onFilterPlatform(platform.id)"
              >{{ platform.title }}</el-button
            >
          </div>
          <div class="search-page__filters">
            <el-button
              class="search-page__filter"
              v-for="status in STATUSES"
              :key="status.type"
              size="small"
              :type="filter.status.has(status.type) ? 'primary' : ''"
              :plain="filter.status.has(status.type)"
              :text="!filter.status.has(status.type)"
              :bg="!filter.status.has(status.type)"
              @click="onFilterStatus(status.type)"
              >{{ status.title }}
              <template #icon v-if="status.color">
                <el-icon size="12"
                  ><div
                    class="circle"
                    :style="`background: ${status.color}`"
                  ></div
                ></el-icon>
              </template>
            </el-button>
            <el-button
              v-if="filter.status.size > 0"
              size="small"
              text
              @click="filter.status = new Set()"
              >Clear</el-button
            >
          </div>
        </div>
        <el-table
          :data="filteredList"
          height="100%"
          style="width: 100%; border-radius: 4px"
          :row-class-name="tableRowClassName"
          @row-click="onClickCeil"
        >
          <template #empty>
            <EmptyData class="search-page__table-empty" />
          </template>
          <el-table-column prop="status" label="" width="44">
            <template #default="scope">
              <StatusTest :status="scope.row.status" />
            </template>
          </el-table-column>
          <el-table-column prop="title" label="Test name" sortable />
          <el-table-column prop="key" label="Unique Key" sortable />
          <el-table-column
            prop="application"
            label="All Apps"
            sortable
            label-class-name="search-page__header-column"
          >
            <template #header>
              <div class="search-page__status">
                {{ filter.applications.size || "All" }} Apps
                <el-icon
                  class="search-page__status-close"
                  v-if="filter.applications.size"
                  @click.stop="filter.applications = new Set()"
                  ><Close
                /></el-icon>
                <el-dropdown trigger="click" @command="onFilterApps">
                  <el-icon
                    class="search-page__status-arrow"
                    color="#909399"
                    @click.stop
                    ><ArrowDown
                  /></el-icon>
                  <template #dropdown>
                    <el-dropdown-menu style="width: 256px">
                      <el-dropdown-item
                        v-for="application in applications"
                        :key="application.id"
                        :command="application.id"
                        class="search-page__dropdown-item"
                        :class="{
                          'search-page__dropdown-item_active':
                            filter.applications.has(application.id),
                        }"
                      >
                        <div class="search-page__dropdown-item-left">
                          <img
                            :src="application.icon_full_url"
                            class="search-page__dropdown-item-image"
                          />
                          <span>{{ application.title }}</span>
                        </div>
                        <el-icon
                          color="#409EFF"
                          class="search-page__dropdown-item-icon"
                          ><Check
                        /></el-icon>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </template>
            <template #default="scope">
              {{ scope.row.application.title }}
            </template>
          </el-table-column>
          <el-table-column
            prop="platforms"
            label="OS"
            label-class-name="search-page__header-column"
          >
            <template #default="scope">
              {{ getTitlePlatforms(scope.row.platforms) }}
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="Start Date" sortable>
            <template #default="scope">
              {{ formatDate(scope.row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column
            prop="custom_status"
            label="All Statuses"
            class-name="search-page__all-status"
            sortable
          >
            <template #header> All Statuses </template>
            <template #default="scope">
              <div class="search-page__status">
                <el-tag
                  class="search-page__tag"
                  :type="getTypeByStatus(scope.row.custom_status)"
                  >{{ scope.row.custom_status || "Ready" }}</el-tag
                >

                <el-dropdown
                  trigger="click"
                  :disabled="isViewer"
                  @command="
                    updateExperiments(scope.row.id, { custom_status: $event })
                  "
                >
                  <el-icon class="search-page__status-arrow" color="#909399"
                    ><ArrowDown
                  /></el-icon>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item
                        v-for="status in STATUSES"
                        :key="status.type"
                        :command="status.type"
                        :disabled="status.type == scope.row.custom_status"
                        >{{ status.title }}</el-dropdown-item
                      >
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="search-page__side">
        <ViewInspector
          v-if="formMode === FORM_MODE.VIEW"
          :id="currentId"
          :status="formMode"
          :field="experiment"
          :statistic="statistic"
          :significance="significance"
          :environment="environment"
          @delete="handleDeleteClick"
          @edit="handleEdit"
          @on-tab-change="handleTabChange"
        />
        <ActionInspector
          v-else-if="
            formMode === FORM_MODE.CREATE || formMode === FORM_MODE.EDIT
          "
          :status="formMode"
          :field="experiment"
          @submit="saveTest"
          @delete="handleDeleteClick"
          @cancel="enableViewMode"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { Plus, Search, ArrowDown, Check, Close } from "@element-plus/icons-vue";
import formatDate from "@/shared/lib/formatDate";
import {
  saveFilterState,
  loadFilterState,
  validateFilterState,
} from "@/shared/lib/filterStorage";
import { StatusTest } from "@/shared/ui/components/StatusTest";
import { EmptyData } from "@/shared/ui/components/EmptyData";
import { ActionInspector } from "@/shared/ui/components/ActionInspector";
import { ViewInspector } from "@/shared/ui/components/ViewInspector";
import { STATUSES_FILTER, STATUSES } from "../const";
import { ElMessage } from "element-plus";
import { FORM_MODE } from "@/shared/constants/mode";
import { cloneDeep } from "lodash";
import { ElMessageBox } from "element-plus";
import { IExperiment } from "@/shared/types/experiments";
import { IPlatform } from "@/shared/types/platform";
import { IVariant } from "@/shared/types/variants";

const store = useStore();
const route = useRoute();
const router = useRouter();
const formMode = ref("");
const loading = ref(false);
const environment = ref("production");

const currentId = ref<number | null>(null);

const filter = reactive({
  search: "",
  status: new Set(),
  applications: new Set(),
  platforms: new Set(),
});

const isQaEngineer = computed(() => {
  return store.state.auth.roles.includes("qa_engineer");
});

const experiments = computed<IExperiment[]>(() => {
  return store.state.experiments.list;
});
const experiment = computed(() => {
  if (currentId.value) {
    const item =
      store.state.experiments.list.find(
        (experiment: IExperiment) => experiment.id === currentId.value
      ) || {};
    if (item) {
      item.platforms_ids = item?.platforms?.map((el: IPlatform) => el.id);
    }
    return item;
  } else {
    return {
      title: "",
      application_id: "",
      status: "draft",
      target: "",
      description: "",
      key: "",
      custom_status: "testing",
      variants: [
        {
          id: Date.now(),
          title: "",
          key: "",
          percentage: 0,
          status: "new",
        },
        {
          id: Date.now() + 1,
          title: "",
          key: "",
          percentage: 0,
          status: "new",
        },
      ],
      platforms_ids: [],
    };
  }
});
const statistic = computed(() => {
  return store.state.experiments.statistic;
});
const significance = computed(() => {
  return store.state.experiments.significance;
});
const applications = computed(() => {
  return store.state.applications.list;
});
const platforms = computed(() => {
  return store.state.platforms.list;
});
const isViewer = computed(() => {
  return store.state.auth.roles.includes("viewer");
});

const filteredList = computed(() => {
  const filtered = experiments.value.filter((item: IExperiment) => {
    if (
      filter.applications.size &&
      !filter.applications.has(item.application.id)
    ) {
      return false;
    }

    if (
      filter.platforms.size &&
      !item.platforms.some((platform) => filter.platforms.has(platform.id))
    ) {
      return false;
    }

    // If no status filters are selected, show all items
    if (filter.status.size === 0) {
      return true;
    }
    // Check if item's status is in the selected statuses
    return filter.status.has(item.custom_status);
  });
  if (!filter.search) {
    return filtered;
  }
  return filtered.filter((item) =>
    item.title.toLowerCase().includes(filter.search.toLowerCase())
  );
});

// Load saved filters after data is loaded
const loadSavedFilters = () => {
  const savedState = loadFilterState();
  if (savedState) {
    // Validate against available options
    const validStatuses = STATUSES_FILTER.map((s) => s.type);
    const availableApps = applications.value.map((app) => app.id);
    const availablePlatforms = platforms.value.map((platform) => platform.id);

    const validatedState = validateFilterState(
      savedState,
      availableApps,
      availablePlatforms,
      validStatuses
    );

    // Apply validated filters
    filter.search = validatedState.search;
    filter.status = validatedState.status; // This is already a Set from validateFilterState
    filter.applications = new Set(validatedState.applications);
    filter.platforms = new Set(validatedState.platforms);
  }
};

// Watch for filter changes and save to localStorage
watch(
  () => ({
    search: filter.search,
    status: Array.from(filter.status),
    applications: Array.from(filter.applications),
    platforms: Array.from(filter.platforms),
  }),
  () => {
    saveFilterState(filter);
  },
  { deep: true }
);

onMounted(async () => {
  try {
    environment.value = isQaEngineer.value ? "test" : "production";
    await getExperiments();
    if (experiments.value.length && experiments.value[0].id) {
      const experimentIdFromQuery: number | null = parseInt(
        route.query.experiment_id as string,
        10
      );
      let selectedExperiment: IExperiment =
        experiments.value.find((exp) => exp.id === experimentIdFromQuery) ||
        experiments.value[0];

      if (!route.query.experiment_id) {
        await router.replace({
          path: route.path,
          query: {
            ...route.query,
            experiment_id: selectedExperiment.id.toString(),
          },
        });
      }
      formMode.value = FORM_MODE.VIEW;

      currentId.value = selectedExperiment.id;

      try {
        await getApplications();
        await getPlatforms();

        // Load saved filters after applications and platforms are loaded
        loadSavedFilters();

        await fetchStatistics(selectedExperiment);
      } catch (e: any) {
        ElMessage({
          message: e,
          type: "error",
        });
      }
    }
  } catch (e: any) {
    ElMessage({
      message: e,
      type: "error",
    });
  }
});

const getExperiments = async () => {
  return await store.dispatch("experiments/getExperiments");
};
const getStatistics = async (payload: {
  experiment_id: number;
  created_at: string;
  environment: string;
}) => {
  return await store.dispatch("experiments/getStatistics", payload);
};
const getStatisticSignificance = async (payload: {
  experiment_id: number;
  environment: string;
}) => {
  return await store.dispatch("experiments/getStatisticSignificance", payload);
};
const getApplications = async () => {
  return await store.dispatch("applications/getApplications");
};
const getPlatforms = async () => {
  return await store.dispatch("platforms/getPlatforms");
};

const updateExperiments = async (id: number, field: { [key: string]: any }) => {
  return await store.dispatch("experiments/updateExperiments", { id, field });
};

const handleDeleteClick = async (id: number) => {
  ElMessageBox.confirm("", "Delete Super Buper Test?", {
    confirmButtonText: "Delete",
    cancelButtonText: "Cancel",
    cancelButtonClass: "cancel-button",
    confirmButtonClass: "confirm-button",
    showClose: false,
  }).then(async () => {
    await store.dispatch("experiments/deleteExperiments", id);
    onClickCeil(experiments.value[0]);
  });
};

const saveTest = (field: IExperiment) => {
  if (formMode.value === FORM_MODE.EDIT) {
    editTest(cloneDeep(field));
  } else {
    createTest({ ...cloneDeep(field), custom_status: "ready" });
  }
};
const createTest = async (val: IExperiment) => {
  try {
    const response = await store.dispatch("experiments/createExperiments", val);

    currentId.value = response.id;
    createVariant(response.id, val.variants);
    router.replace({
      path: route.path,
      query: { ...route.query, experiment_id: response.id },
    });

    // Switch to view mode and make sure the new experiment is selected
    formMode.value = FORM_MODE.VIEW;
    await getExperiments(); // Refresh experiments list to ensure new experiment is included
    onClickCeil(response, "new"); // Use the response which contains the newly created experiment
  } catch (e: any) {
    if (Array.isArray(e)) {
      e.forEach((error) => {
        ElMessage({
          message: error,
          type: "error",
        });
      });
    } else {
      ElMessage({
        message: e,
        type: "error",
      });
    }
  }
};
const editTest = async (val: IExperiment) => {
  try {
    loading.value = true;
    const response = await store.dispatch("experiments/editExperiments", {
      id: experiment.value.id,
      field: { ...val, custom_status: experiment.value.custom_status },
    });
    await createVariant(response.id, val.variants);
    await getStatistics({
      experiment_id: response.id,
      created_at: response.created_at,
      environment: environment.value,
    });
    formMode.value = FORM_MODE.VIEW;
  } catch (e: any) {
    if (Array.isArray(e)) {
      e.forEach((error) => {
        ElMessage({
          message: error,
          type: "error",
        });
      });
    } else {
      ElMessage({
        message: e,
        type: "error",
      });
    }
  } finally {
    loading.value = false;
  }
};

const createVariant = (experimentId: number, variants: IVariant[]) => {
  try {
    store
      .dispatch("experiments/createVariant", {
        experiment_id: experimentId,
        variants: variants.map((item) => {
          return {
            id: item.id,
            key: item.key,
            percentage: item.percentage,
            title: item.title,
          };
        }),
      })
      .then((response) => {
        experiment.value.variants = response.data.map((item: IVariant) => {
          return {
            id: item.id,
            title: item.title,
            key: item.key,
            percentage: item.percentage,
            status: "edit",
          };
        });
      });
  } catch (e: any) {
    ElMessage({
      message: e,
      type: "error",
    });
  }
};

const handleEdit = () => {
  formMode.value = FORM_MODE.EDIT;
};

const tableRowClassName = ({ row }: { row: IExperiment }) => {
  return row.id === currentId.value
    ? "table-row table-row--selected"
    : "table-row";
};

const addTest = () => {
  formMode.value = FORM_MODE.CREATE;
  currentId.value = null;
};

const handleTabChange = (env: string) => {
  environment.value = env;
  const experiment = experiments.value.find(
    (item: IExperiment) => item.id === currentId.value
  );
  if (experiment) {
    fetchStatistics(experiment);
  }
};

const onClickCeil = async (item: IExperiment, status?: string) => {
  formMode.value = FORM_MODE.VIEW;

  if (experiment.value.id !== item.id || status === "new") {
    try {
      currentId.value = item.id;
      environment.value = isQaEngineer.value ? "test" : "production";

      await fetchStatistics(item);
    } catch (e: any) {
      ElMessage({
        message: e,
        type: "error",
      });
    } finally {
      router.replace({
        path: route.path,
        query: { ...route.query, experiment_id: item.id },
      });
    }
  }
};

const fetchStatistics = async (item: IExperiment) => {
  await getStatistics({
    experiment_id: item.id,
    created_at: item.created_at,
    environment: environment.value,
  });
  await getStatisticSignificance({
    experiment_id: item.id,
    environment: environment.value,
  });
};

const getTitlePlatforms = (arr: IPlatform[] = []) => {
  if (arr.length === platforms.value.length) {
    return "All";
  }
  return arr.map((el) => el.title).join(", ");
};

const getTypeByStatus = (status: string) => {
  switch (status) {
    case "ready":
      return "primary";
    case "testing":
      return "success";
    case "done":
      return "info";
    default:
      return "primary";
  }
};

const onFilterPlatform = (id: number) => {
  if (filter.platforms.has(id)) {
    filter.platforms.delete(id);
  } else {
    filter.platforms.add(id);
  }
};

const onFilterApps = (id: number) => {
  if (filter.applications.has(id)) {
    filter.applications.delete(id);
  } else {
    filter.applications.add(id);
  }
};

const onFilterStatus = (statusType: string) => {
  if (filter.status.has(statusType)) {
    filter.status.delete(statusType);
  } else {
    filter.status.add(statusType);
  }
};

const enableViewMode = () => {
  formMode.value = FORM_MODE.VIEW;
};
</script>

<style lang="scss">
.search-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: 24px;
    height: 56px;
    flex-shrink: 0;
    margin-bottom: 16px;
  }
  &__title {
    font-size: 18px;
    font-weight: 800;
    line-height: 26px;
  }
  &__top-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__main {
    display: flex;
    flex-grow: 1;
    overflow: hidden;
  }
  &__content {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-grow: 1;
    width: 100%;
    height: 100%;
    border-radius: var(--Radius-border-radius-base, 4px);
    background: var(--Color-Background-bg-color, #fff);
  }
  &__content-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid var(--Color-Border-border-color-lighter, #ebeef5);
  }
  &__filters {
    display: flex;
    align-items: center;
    gap: 16px;
    .el-button + .el-button {
      margin-left: 0;
    }
  }
  &__filters-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__button-link .el-icon {
    color: #409eff;
  }
  &__app-title {
    color: var(--Color-Primary-color-primary, #409eff);
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
  }
  &__app-subtitle {
    color: var(--Color-Text-text-color-regular, #606266);
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
  }
  &__side {
    position: relative;
    flex-shrink: 0;
    height: 100%;
    width: 344px;
    padding: 0 24px;
    overflow: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  &__side-line {
    height: 1px;
    background: var(--Color-Border-border-color-light, #e4e7ed);
    margin: 16px 0;
  }
  &__table-empty {
    margin: 0 auto;
  }
  &__status {
    display: flex;
    align-items: center;
  }
  &__status-close {
    margin-left: 8px;
  }
  &__status-arrow {
    cursor: pointer;
    margin-left: 8px !important;
  }
  &__dropdown-item-icon {
    display: none !important;
  }
  &__dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    span {
      color: var(--Color-Text-text-color-regular, #606266);
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
    }
    &_active {
      span {
        color: var(--Color-Primary-color-primary, #409eff);
      }
      .search-page__dropdown-item-icon {
        display: block !important;
      }
    }
  }
  &__dropdown-item-left {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  &__dropdown-item-image {
    width: var(--Size-common-component-size-small, 24px);
    height: var(--Size-common-component-size-small, 24px);
    flex-shrink: 0;
  }
  &__header-column {
    .cell {
      display: flex;
      align-items: center;
    }
  }
  &__tag {
    span::first-letter {
      text-transform: uppercase;
    }
  }
  &__all-status {
    .search-page__status-arrow {
      display: none;
    }
  }

  .table-row {
    &:hover {
      .search-page__all-status .search-page__status-arrow {
        display: block;
      }
    }

    &--selected {
      background-color: var(--el-color-primary-light-9) !important;
      border-left: 2px solid var(--el-color-primary);
    }
  }

  .circle {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 100%;
  }
}
</style>
